import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import {
    Avatar,
    Box,
    Button,
    Container,
    CssBaseline,
    Link,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import {useTranslation} from 'react-i18next';

import {useApplicationContext} from '../../ApplicationContext';
import {Copyright} from '../../Components/Copyright';
import LanguageButton from '../../Components/LanguageButton';
import {SupportIcon} from '../../SupportIcon';

export const MfaVerify = () => {
    const context = useApplicationContext();
    const {t} = useTranslation();
    const [code, setCode] = useState('');
    const [useRecovery, setUseRecovery] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, [useRecovery]);

    const isReady = useRecovery
        ? code.trim().length >= 8
        : /^\d{6}$/.test(code);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!isReady || submitting) return;
        setSubmitting(true);
        try {
            await context.verifyMfa(code.trim());
        } finally {
            setSubmitting(false);
            setCode('');
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (useRecovery) {
            setCode(value.toUpperCase().replace(/\s+/g, ''));
        } else {
            setCode(value.replace(/\D/g, '').slice(0, 6));
        }
    };

    return (
        <Container component="main" maxWidth="xs" sx={{display: 'flex', flexDirection: 'column', minHeight: '100vh'}}>
            <CssBaseline/>
            <Box sx={{flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 4}}>
                <Paper
                    elevation={3}
                    sx={{
                        p: {xs: 3, sm: 4},
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <SupportIcon size={80}/>
                    <Avatar sx={{mt: 2, bgcolor: 'primary.main'}}>
                        <ShieldOutlinedIcon/>
                    </Avatar>
                    <Typography component="h1" variant="h5" sx={{mt: 2, textAlign: 'center'}}>
                        {t('mfa.verify.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{mt: 1, textAlign: 'center'}}>
                        {useRecovery
                            ? t('mfa.verify.recoveryHint')
                            : t('mfa.verify.codeHint')}
                    </Typography>

                    <Box component="form" onSubmit={handleSubmit} noValidate sx={{mt: 3, width: '100%'}}>
                        <TextField
                            inputRef={inputRef}
                            required
                            fullWidth
                            autoFocus
                            autoComplete="one-time-code"
                            value={code}
                            onChange={handleChange}
                            label={useRecovery ? t('mfa.verify.recoveryLabel') : t('mfa.verify.codeLabel')}
                            placeholder={useRecovery ? 'XXXXXXXX' : '••••••'}
                            inputMode={useRecovery ? 'text' : 'numeric'}
                            slotProps={{
                                htmlInput: {
                                    maxLength: useRecovery ? 32 : 6,
                                    style: {
                                        letterSpacing: useRecovery ? '0.15em' : '0.5em',
                                        textAlign: 'center',
                                        fontSize: useRecovery ? '1.2rem' : '1.6rem',
                                        fontFamily: 'monospace',
                                    },
                                },
                            }}
                        />

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={!isReady || submitting}
                            sx={{mt: 3, py: 1.2}}
                        >
                            {submitting ? t('mfa.verify.submitting') : t('mfa.verify.submit')}
                        </Button>

                        <Box sx={{mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <Link
                                component="button"
                                type="button"
                                variant="body2"
                                onClick={() => {
                                    setUseRecovery(!useRecovery);
                                    setCode('');
                                }}
                            >
                                {useRecovery ? t('mfa.verify.useCode') : t('mfa.verify.useRecovery')}
                            </Link>
                            <Link
                                component="button"
                                type="button"
                                variant="body2"
                                color="text.secondary"
                                onClick={context.cancelMfa}
                            >
                                {t('mfa.verify.cancel')}
                            </Link>
                        </Box>
                    </Box>
                </Paper>
                <LanguageButton floating/>
            </Box>
            <Box sx={{pb: 2}}>
                <Copyright/>
            </Box>
        </Container>
    );
};

export default MfaVerify;
